import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLicense } from '../contexts/LicenseContext';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import { useSession } from '../contexts/SessionContext';

interface GettingStartedGuideProps {
  /** True while the unified sign-in dialog is open (App state). */
  signInOpen: boolean;
  /** True while the trial registration dialog is open (App state). */
  trialOpen: boolean;
  onOpenSignIn: () => void;
  onStartTrial: () => void;
}

type StageId =
  | 'preview'
  | 'signin'
  | 'license'
  | 'upload'
  | 'fields'
  | 'save'
  | 'generate'
  | 'done';

const STAGE_ORDER: StageId[] = ['signin', 'license', 'upload', 'fields', 'save', 'preview', 'generate', 'done'];

/**
 * What a visitor with no licence is actually walking through.
 *
 * The account stages are not in it. A guest is never authenticated and never
 * licensed, so they were unreachable states dressed as steps - and because the
 * dots come from this list, a guest saw "step 4 of 7" having skipped two they
 * could never have been on. The guide has to describe the journey the person
 * is on, not the one a licensed user would be.
 *
 * The account moment has not disappeared; it lives at the artifact gates,
 * where something of theirs is being held. Section 1: the wall is at the
 * artifacts, not the door.
 */
// A guest cannot generate a project, so 'generate' is not in their journey.
// 'preview' is - the flattened preview is free, and it is the moment the
// product proves itself.
const GUEST_STAGE_ORDER: StageId[] = ['upload', 'fields', 'save', 'preview', 'done'];

const COLLAPSED_KEY = 'pfbGuideCollapsed';

type Corner = 'tl' | 'tr' | 'bl' | 'br';
const CORNER_KEY = 'pfbGuideCorner';
// Top corners sit below the sticky h-16 header so the guide never covers it.
const CORNER_CLASSES: Record<Corner, string> = {
  br: 'bottom-4 right-4',
  bl: 'bottom-4 left-4',
  tr: 'top-20 right-4',
  tl: 'top-20 left-4',
};

/**
 * Non-modal, stage-aware onboarding companion.
 *
 * Floats bottom-right above dialog backdrops (guide z-[60] vs dialog z-50) so
 * a new user can read the current step while typing into the sign-in or trial
 * dialog. Stages advance from real application state — auth, license, upload,
 * fields, saved session, generation — never from "Next" clicks, so the guide
 * can never run ahead of or behind the user.
 *
 * Reopen affordances: the collapsed pill (always rendered) and the Guide
 * button in the header, which dispatches `pfb-toggle-guide`.
 * PDFUpload dispatches `pfb-project-generated` on a successful project
 * generation to drive the final stage.
 */
const GettingStartedGuide: React.FC<GettingStartedGuideProps> = ({
  signInOpen,
  trialOpen,
  onOpenSignIn,
  onStartTrial,
}) => {
  const { state: auth } = useAuth();
  const { licenseInfo } = useLicense();
  const { state: session } = useSession();
  const { capabilities } = useCapabilities();

  // Which screen is showing. PDFUpload owns this and announces it, because the
  // guide advances from application STATE and is otherwise blind to where the
  // user is actually looking.
  const [activeView, setActiveView] = useState<'pdf' | 'form'>('pdf');
  useEffect(() => {
    const handler = (e: Event) => {
      const view = (e as CustomEvent)?.detail?.view;
      if (view === 'pdf' || view === 'form') setActiveView(view);
    };
    window.addEventListener('pfb-view-changed', handler);
    return () => window.removeEventListener('pfb-view-changed', handler);
  }, []);

  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(COLLAPSED_KEY) === 'true'
  );
  const [route, setRoute] = useState<'license' | 'trial'>('license');
  const [generated, setGenerated] = useState(false);
  // null = follow the live stage; a StageId = the user is browsing manually
  const [viewedStage, setViewedStage] = useState<StageId | null>(null);
  // Set when trial registration succeeds (payment done, license issued).
  // Survives a refresh of this tab via sessionStorage. Trial accounts start
  // email-unverified, so the guide narrates verify-then-sign-in from here.
  const [trialOutcome, setTrialOutcome] = useState<{ email: string; licenseApplied: boolean } | null>(() => {
    try {
      const raw = sessionStorage.getItem('pfbTrialRegistered');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  // True once we know (or the user confirms) the verification link was clicked.
  const [emailVerified, setEmailVerified] = useState(false);

  // Where the guide (card and pill) docks. Drag the card's header anywhere and
  // it snaps to the nearest corner on release — free positions would go stale
  // on resize; a corner is always fully on-screen, including small screens.
  const [corner, setCorner] = useState<Corner>(() => {
    const stored = localStorage.getItem(CORNER_KEY);
    return stored === 'tl' || stored === 'tr' || stored === 'bl' || stored === 'br' ? stored : 'br';
  });
  const [dragging, setDragging] = useState(false);
  const [dragXY, setDragXY] = useState({ x: 0, y: 0 });
  const dragXYRef = useRef({ x: 0, y: 0 });
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);

  const beginDrag = (clientX: number, clientY: number, target: EventTarget | null) => {
    // Buttons in the header (collapse ✕) keep their click behavior.
    if (target instanceof HTMLElement && target.closest('button')) return;
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffsetRef.current = { x: clientX - rect.left, y: clientY - rect.top };
    dragXYRef.current = { x: rect.left, y: rect.top };
    setDragXY({ x: rect.left, y: rect.top });
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (clientX: number, clientY: number) => {
      const next = { x: clientX - dragOffsetRef.current.x, y: clientY - dragOffsetRef.current.y };
      dragXYRef.current = next;
      setDragXY(next);
    };
    const finish = () => {
      const rect = cardRef.current?.getBoundingClientRect();
      const cx = rect ? rect.left + rect.width / 2 : dragXYRef.current.x;
      const cy = rect ? rect.top + rect.height / 2 : dragXYRef.current.y;
      const next: Corner = `${cy < window.innerHeight / 2 ? 't' : 'b'}${cx < window.innerWidth / 2 ? 'l' : 'r'}` as Corner;
      setCorner(next);
      localStorage.setItem(CORNER_KEY, next);
      setDragging(false);
    };
    const onMouseMove = (e: MouseEvent) => move(e.clientX, e.clientY);
    const onMouseUp = () => finish();
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault(); // keep the page from scrolling while dragging
      move(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchEnd = () => finish();
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [dragging]);

  // Header "Guide" button and any other affordance toggle via this event.
  useEffect(() => {
    const toggle = () => {
      setCollapsed(prev => {
        localStorage.setItem(COLLAPSED_KEY, String(!prev));
        return !prev;
      });
    };
    window.addEventListener('pfb-toggle-guide', toggle);
    return () => window.removeEventListener('pfb-toggle-guide', toggle);
  }, []);

  useEffect(() => {
    const onGenerated = () => setGenerated(true);
    window.addEventListener('pfb-project-generated', onGenerated);
    return () => window.removeEventListener('pfb-project-generated', onGenerated);
  }, []);

  useEffect(() => {
    const onTrialRegistered = (e: Event) => {
      const detail = (e as CustomEvent).detail as { email?: string; licenseApplied?: boolean } | undefined;
      const outcome = { email: detail?.email ?? '', licenseApplied: detail?.licenseApplied === true };
      setTrialOutcome(outcome);
      setRoute('trial');
      try {
        sessionStorage.setItem('pfbTrialRegistered', JSON.stringify(outcome));
      } catch { /* non-fatal */ }
    };
    window.addEventListener('pfb-trial-registered', onTrialRegistered);
    return () => window.removeEventListener('pfb-trial-registered', onTrialRegistered);
  }, []);

  // Email-verification signals: same-tab (the verification landing dispatches
  // pfb-email-verified) and cross-tab within the same browser (storage event).
  // The mail client may open a different browser entirely, so the guide also
  // offers a manual "Account has been verified" confirmation.
  useEffect(() => {
    const onVerified = () => setEmailVerified(true);
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'pfbEmailVerifiedAt' && e.newValue) setEmailVerified(true);
    };
    window.addEventListener('pfb-email-verified', onVerified);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('pfb-email-verified', onVerified);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // The guide follows the user's action: opening a dialog selects its route.
  // Exception: once trial registration happened, the trial path (whose final
  // step IS signing in) must keep narrating — opening the sign-in dialog to
  // finish that step must not clobber it with the generic license route.
  useEffect(() => {
    if (signInOpen && !trialOutcome) setRoute('license');
  }, [signInOpen, trialOutcome]);
  useEffect(() => {
    if (trialOpen) setRoute('trial');
  }, [trialOpen]);

  const tier = licenseInfo?.tier?.toUpperCase();
  const hasLicense = !!tier && tier !== 'ANONYMOUS' && tier !== 'NO_LICENSE';
  const hasDocument = !!session.uploadResult.documentId;
  const hasFields = session.selectedFields.length > 0;
  const hasSavedSession = !!session.currentSessionId;

  const isGuest = capabilities?.identity === 'ANONYMOUS';

  const stage: StageId = useMemo(() => {
    // A guest is never authenticated and never licensed, so the original first
    // two lines pinned them on 'signin' for the WHOLE journey - upload, fields
    // and save advanced nothing. The guide sat on step 1 of 7 describing an
    // account while the visitor built a form beside it.
    //
    // For a guest the account steps are not gates to pass, so the guide follows
    // the WORK instead. The account moment still arrives, at the artifact gates
    // where something of theirs is actually being held.
    // Looking at the Live Form Preview is an ACTIVITY, not a step forward, so
    // it overrides sequence position. Someone filling in values should be told
    // about filling in values, not about the screen they just left.
    if (activeView === 'form' && hasDocument) return 'preview';

    if (isGuest) {
      if (!hasDocument) return 'upload';
      if (!hasFields) return 'fields';
      if (!hasSavedSession) return 'save';
      if (!generated) return 'generate';
      return 'done';
    }

    if (!auth.isAuthenticated) return 'signin';
    if (!hasLicense) return 'license';
    if (!hasDocument) return 'upload';
    if (!hasFields) return 'fields';
    if (!hasSavedSession) return 'save';
    if (!generated) return 'generate';
    return 'done';
  }, [activeView, isGuest, auth.isAuthenticated, hasLicense, hasDocument, hasFields, hasSavedSession, generated]);

  // The active sequence. Everything downstream - dots, bounds, manual
  // navigation, the "step N of M" label - reads this rather than the licensed
  // list, so a guest's progress is measured against their own journey.
  const stages = isGuest ? GUEST_STAGE_ORDER : STAGE_ORDER;

  const stageIndex = stages.indexOf(stage);

  // If the live stage catches up to (or passes through) the browsed stage,
  // resume following automatically.
  useEffect(() => {
    if (viewedStage !== null && viewedStage === stage) {
      setViewedStage(null);
    }
  }, [stage, viewedStage]);

  // Manual navigation (backup for the automatic stage tracking): arrows and
  // clickable dots. 'done' is only reachable once it is genuinely the live
  // stage — its content asserts a generated project.
  const maxNavIndex = stage === 'done' ? stages.length - 1 : stages.length - 2;
  const displayStage = viewedStage ?? stage;
  const displayIndex = stages.indexOf(displayStage);

  const goToIndex = (target: number) => {
    const clamped = Math.max(0, Math.min(maxNavIndex, target));
    setViewedStage(clamped === stageIndex ? null : stages[clamped]);
  };

  const setCollapsedPersisted = (value: boolean) => {
    localStorage.setItem(COLLAPSED_KEY, String(value));
    setCollapsed(value);
  };

  // Collapsed: a small always-available pill.
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsedPersisted(false)}
        className={`fixed ${CORNER_CLASSES[corner]} z-[60] flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium pl-3 pr-4 py-2.5 rounded-full shadow-lg transition-colors`}
        title="Open the step-by-step guide"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
        Guide
      </button>
    );
  }

  const stepBase = 'flex gap-2 text-sm text-neutral-700 leading-snug';
  const num = (n: number) => (
    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center mt-0.5">
      {n}
    </span>
  );
  const check = (
    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-success-100 text-success-600 flex items-center justify-center mt-0.5">
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
      </svg>
    </span>
  );

  const stageBody = () => {
    switch (displayStage) {
      case 'signin':
        return (
          <>
            {/* The panel below is the account route, and it is no longer a
                prerequisite. Saying so first stops the guide contradicting the
                chooser sitting next to it, which invites an upload with no
                account at all. */}
            <p className="text-xs text-neutral-600 mb-3">
              Upload a PDF and build your form right now — no account required.
              You'll only need one to download the finished file, and your work
              comes with you when you do.
            </p>
            <div className="grid grid-cols-2 gap-1 bg-neutral-100 rounded-lg p-1 mb-3">
              <button
                type="button"
                onClick={() => setRoute('license')}
                className={`text-xs font-semibold rounded-md py-1.5 transition-colors ${
                  route === 'license' ? 'bg-white text-primary-700 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
                }`}
              >
                I have a license
              </button>
              <button
                type="button"
                onClick={() => setRoute('trial')}
                className={`text-xs font-semibold rounded-md py-1.5 transition-colors ${
                  route === 'trial' ? 'bg-white text-primary-700 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
                }`}
              >
                I'm new — free trial
              </button>
            </div>
            {route === 'trial' && trialOutcome ? (
              // Trial registered: payment done, license issued and (usually)
              // auto-applied. Trial accounts start email-unverified, so the
              // path from here is verify → sign in.
              <div className="space-y-2.5">
                <div className={stepBase}>
                  {check}
                  <span>Account created</span>
                </div>
                <div className={stepBase}>
                  {check}
                  <span>
                    Payment confirmed — <b>your trial license is active</b>
                    {trialOutcome.licenseApplied ? ' and already applied here' : ''}
                  </span>
                </div>
                <div className={stepBase}>
                  {emailVerified ? check : num(3)}
                  <span>
                    Check your inbox{trialOutcome.email ? <> at <b>{trialOutcome.email}</b></> : null} and
                    click the <b>verification link</b>
                  </span>
                </div>
                {!emailVerified && (
                  <div className="pl-7">
                    <button
                      type="button"
                      onClick={() => setEmailVerified(true)}
                      className="text-xs font-semibold text-primary-600 hover:underline"
                    >
                      Account has been verified →
                    </button>
                  </div>
                )}
                <div className={stepBase}>
                  {num(4)}
                  <span>
                    {emailVerified ? <>Now click{' '}</> : <>Then click{' '}</>}
                    <button type="button" onClick={onOpenSignIn} className="text-primary-600 font-semibold hover:underline">
                      Sign In
                    </button>{' '}
                    and enter your <b>email</b> and <b>password</b>
                  </span>
                </div>
                <p className="text-xs text-neutral-500 pt-1 border-t border-neutral-100">
                  Keep the license key from the dialog somewhere safe — it's also in your welcome email.
                </p>
              </div>
            ) : route === 'license' ? (
              <div className="space-y-2.5">
                <div className={stepBase}>
                  {signInOpen ? check : num(1)}
                  <span>
                    Click{' '}
                    <button type="button" onClick={onOpenSignIn} className="text-primary-600 font-semibold hover:underline">
                      Sign In
                    </button>
                  </span>
                </div>
                <div className={stepBase}>
                  {num(2)}
                  <span>Enter your <b>license key</b>, <b>email</b>, and <b>password</b> — from your admin or your purchase email</span>
                </div>
                <div className={stepBase}>
                  {num(3)}
                  <span>That's it — this guide follows you to the next step</span>
                </div>
                <p className="text-xs text-neutral-500 pt-1 border-t border-neutral-100">
                  No account yet? Your company's admin adds users in the customer portal.
                </p>
              </div>
            ) : (
              <div className="space-y-2.5">
                <div className={stepBase}>
                  {trialOpen ? check : num(1)}
                  <span>
                    Click{' '}
                    <button type="button" onClick={onStartTrial} className="text-primary-600 font-semibold hover:underline">
                      Start Free Trial
                    </button>
                  </span>
                </div>
                <div className={stepBase}>
                  {num(2)}
                  <span>Create your account — name, email, company, password</span>
                </div>
                <div className={stepBase}>
                  {num(3)}
                  <span>
                    Add payment details. <b>Your card is not charged</b> until the 7-day trial ends — cancel anytime before then
                  </span>
                </div>
                <div className={stepBase}>
                  {num(4)}
                  <span>You're signed in automatically with your trial license</span>
                </div>
              </div>
            )}
          </>
        );
      case 'license':
        return (
          <div className="space-y-2.5">
            <div className={stepBase}>
              {num(1)}
              <span>
                You're signed in but no license is active. Open the{' '}
                <button type="button" onClick={onOpenSignIn} className="text-primary-600 font-semibold hover:underline">
                  License dialog
                </button>
              </span>
            </div>
            <div className={stepBase}>
              {num(2)}
              <span>Enter your license key (format <span className="font-mono text-xs">CAVU-XXXX-…</span>)</span>
            </div>
            <div className={stepBase}>
              {num(3)}
              <span>No key? Start a free trial from the same dialog</span>
            </div>
          </div>
        );
      case 'upload':
        return (
          <div className="space-y-2.5">
            <div className={stepBase}>
              {num(1)}
              <span>Choose <b>New Project</b> (or load a saved session to continue earlier work)</span>
            </div>
            <div className={stepBase}>
              {num(2)}
              <span>Drag a PDF onto the upload area — or click it to browse (max 25MB)</span>
            </div>
            <div className={stepBase}>
              {num(3)}
              <span>Give the project a name — it will name your generated application</span>
            </div>
            <div className={stepBase}>
              {num(4)}
              <span>Click <b>Upload PDF</b></span>
            </div>
            <p className="text-xs text-neutral-500 pt-1 border-t border-neutral-100">
              {isGuest
                ? 'No account needed to build — you only need one to download the finished file, and your work comes with you.'
                : 'Tip: start with a simple 1–2 page form.'}
            </p>
          </div>
        );
      case 'fields':
        return (
          <div className="space-y-2.5">
            <div className={stepBase}>
              {num(1)}
              <span><b>Drag</b> on the PDF to draw your first field — a bare click does nothing</span>
            </div>
            <div className={stepBase}>
              {num(2)}
              <span>Field Properties opens on the left — type the field's name and pick its type</span>
            </div>
            <div className={stepBase}>
              {num(3)}
              <span>To move around: switch the toolbar to <b>Pan</b> (or hold <b>Space</b>) and drag; <b>Ctrl</b>+scroll zooms</span>
            </div>
          </div>
        );
      case 'save':
        return (
          <div className="space-y-2.5">
            <div className={stepBase}>
              {num(1)}
              <span>Add and adjust fields — click one to edit name, type, and exact position (X/Y/W/H)</span>
            </div>
            <div className={stepBase}>
              {num(2)}
              <span>Click <b>Save Session</b> (above the viewer) and name your work</span>
            </div>
            <div className={stepBase}>
              {num(3)}
              <span>Every save is a new revision — you can always load an earlier one</span>
            </div>
            <p className="text-xs text-neutral-500 pt-1 border-t border-neutral-100">
              Saving is required before generating a project.
            </p>
          </div>
        );
      case 'preview':
        return (
          <div className="space-y-2.5">
            <div className={stepBase}>
              {num(1)}
              <span>Type into the fields to see how your values land on the form</span>
            </div>
            <div className={stepBase}>
              {num(2)}
              <span>
                Click <b>Preview flattened PDF</b> to see the finished document,
                rendered exactly as it will look
              </span>
            </div>
            {isGuest ? (
              <>
                <div className={stepBase}>
                  {num(3)}
                  <span>
                    Downloading it needs an account &mdash; the preview itself is free,
                    and there is no limit on how often you look
                  </span>
                </div>
                <p className="text-xs text-neutral-500 pt-1 border-t border-neutral-100">
                  Your PDF and the fields you&rsquo;ve built come with you when you sign up.
                </p>
              </>
            ) : (
              <>
                <div className={stepBase}>
                  {num(3)}
                  <span>
                    Use <b>Generate Fillable PDF</b> or <b>Generate Flattened PDF</b> to
                    download the document itself
                  </span>
                </div>
                <p className="text-xs text-neutral-500 pt-1 border-t border-neutral-100">
                  Switch back to the editor to adjust field positions, then preview again.
                </p>
              </>
            )}
          </div>
        );
      case 'generate':
        return (
          <div className="space-y-2.5">
            <div className={stepBase}>
              {num(1)}
              <span>Find the <b>Generate Output</b> card above the editor</span>
            </div>
            <div className={stepBase}>
              {num(2)}
              <span>Optional: <b>Open Live Form Preview</b> to fill in values and produce test PDFs</span>
            </div>
            <div className={stepBase}>
              {num(3)}
              <span><b>Generate Spring Boot Project</b> — set the flattened-PDF allocation and confirm; the ZIP downloads automatically</span>
            </div>
          </div>
        );
      case 'done':
        return (
          <div className="space-y-2.5">
            <div className={stepBase}>
              {check}
              <span>You generated a complete application — nicely done!</span>
            </div>
            <div className={stepBase}>
              {num(1)}
              <span>Extract the ZIP and run <span className="font-mono text-xs">docker-start</span> — the README inside covers the rest</span>
            </div>
            <div className={stepBase}>
              {num(2)}
              <span>This guide stays in the corner — reopen it anytime from the pill or the header's <b>Guide</b> button</span>
            </div>
          </div>
        );
    }
  };

  const stageTitle: Record<StageId, string> = {
    // A guest reaches this stage, and only this one - `stage` returns 'signin'
    // whenever auth.isAuthenticated is false, and 'license' is only reachable
    // once signed in. So this title is the one an anonymous visitor reads, and
    // "Sign in to get started" stopped being true when the account wall moved
    // off the door (ANONYMOUS_EVALUATION_MODE.md section 1).
    signin: 'Start building — no account needed',
    license: 'Activate your license',
    upload: 'Upload your PDF',
    fields: 'Draw your form fields',
    save: 'Configure & save',
    preview: 'See your finished PDF',
    generate: 'Generate your project',
    done: 'All done',
  };

  return (
    <div
      ref={cardRef}
      className={`fixed ${dragging ? '' : CORNER_CLASSES[corner]} z-[60] w-80 max-w-[calc(100vw-2rem)] bg-white border border-neutral-200 rounded-2xl shadow-2xl overflow-hidden`}
      style={dragging ? { left: dragXY.x, top: dragXY.y } : undefined}
    >
      <div
        className={`flex items-center justify-between bg-primary-600 text-white px-4 py-2.5 select-none ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={(e) => beginDrag(e.clientX, e.clientY, e.target)}
        onTouchStart={(e) => beginDrag(e.touches[0].clientX, e.touches[0].clientY, e.target)}
        title="Drag to move the guide to another corner"
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <span className="text-sm font-semibold">Getting started</span>
        </div>
        <button
          type="button"
          onClick={() => setCollapsedPersisted(true)}
          className="text-primary-100 hover:text-white transition-colors p-1"
          aria-label="Collapse guide"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Stage navigation: arrows + clickable progress dots. Dots show live
          progress (green = done, blue = current); a ring marks the stage
          being browsed when it differs from the live one. */}
      <div className="flex items-center justify-between px-3 pt-2.5">
        <button
          type="button"
          onClick={() => goToIndex(displayIndex - 1)}
          disabled={displayIndex === 0}
          className="p-1 rounded-md text-neutral-500 hover:text-primary-600 hover:bg-neutral-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          aria-label="Previous step"
          title="Previous step"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-1.5">
          {stages.map((s, i) => (
            <button
              key={s}
              type="button"
              onClick={() => goToIndex(i)}
              disabled={i > maxNavIndex}
              title={stageTitle[s]}
              aria-label={`Go to step ${i + 1}: ${stageTitle[s]}`}
              className={`h-2 rounded-full transition-all disabled:cursor-default ${
                i < stageIndex ? 'bg-success-500' : i === stageIndex ? 'bg-primary-500' : 'bg-neutral-200'
              } ${i === stageIndex ? 'w-5' : 'w-3.5'} ${
                viewedStage !== null && i === displayIndex ? 'ring-2 ring-primary-400 ring-offset-1' : ''
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => goToIndex(displayIndex + 1)}
          disabled={displayIndex >= maxNavIndex}
          className="p-1 rounded-md text-neutral-500 hover:text-primary-600 hover:bg-neutral-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          aria-label="Next step"
          title="Next step"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Browsing another step: offer the way back to the live one */}
      {viewedStage !== null && (
        <div className="flex items-center justify-between mx-4 mt-2 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
          <span className="text-xs text-amber-800">Viewing step {displayIndex + 1} of {stages.length}</span>
          <button
            type="button"
            onClick={() => setViewedStage(null)}
            className="text-xs font-semibold text-primary-700 hover:underline"
          >
            Back to current step
          </button>
        </div>
      )}

      <div className="px-4 py-3 max-h-[60vh] overflow-y-auto">
        <h3 className="text-sm font-bold text-neutral-900 mb-2.5">
          {displayStage === 'signin' && route === 'trial' && trialOutcome
            ? emailVerified
              ? 'Trial active — finish signing in'
              : 'Trial active — verify your email'
            : stageTitle[displayStage]}
        </h3>
        {stageBody()}
      </div>
    </div>
  );
};

export default GettingStartedGuide;
